import { call, put, takeLatest } from "redux-saga/effects";
import {
  onGetRealWeddings,
  onGetRealWeddingsSuccess,
  onGetRealWeddingsError,
  onGetRealWedding,
  onGetRealWeddingSuccess,
  onGetRealWeddingError,
  onRealWeddingPost,
  onRealWeddingPostSuccess,
  onRealWeddingPostError,
} from "../Store/Slices/realWeddingSlice";
import {
  callGetRealWeddingsApi,
  callGetRealWeddingApi,
  callRealWeddingPostFormDataApi,
  callRealWeddingUpdateFormDataApi,
} from "../Context/businessApi";

function isSuccessCode(code) {
  return code === 200 || code === "200" || code === 201 || code === "201";
}

function normalizeSlug(value) {
  if (value == null) return "";
  try {
    return decodeURIComponent(String(value)).trim().toLowerCase();
  } catch {
    return String(value).trim().toLowerCase();
  }
}

function* resolveRealWeddingIdFromSlug(slug) {
  const normalizedSlug = normalizeSlug(slug);
  if (!normalizedSlug) return null;
  const listResponse = yield call(callGetRealWeddingsApi, {});
  const listCode =
    listResponse.httpStatusCode ?? listResponse.status ?? listResponse.statusCode;
  if (!isSuccessCode(listCode)) return null;
  const data =
    listResponse.data?.real_weddings ??
    listResponse.data ??
    listResponse.real_weddings ??
    listResponse;
  const rawList = Array.isArray(data) ? data : data?.data ?? data?.list ?? [];
  const list = Array.isArray(rawList) ? rawList : [];
  const matched = list.find((item) => {
    if (item?.content_type !== "blog") return false;
    return normalizeSlug(item?.slug) === normalizedSlug;
  });
  return matched?.id ?? matched?.real_wedding_id ?? null;
}

/**
 * Real wedding POST: one API only. Full payload (heading, description, wedding_photos with images)
 * is sent as FormData to the real wedding endpoint. No image upload API is called; backend handles images.
 */
function buildRealWeddingFormData(payload) {
  const formData = new FormData();
  const contentType = payload.content_type === "blog" ? "blog" : "real_wedding";
  formData.append("content_type", contentType);
  formData.append("heading", payload.heading ?? "");
  formData.append("city", payload.city ?? "");
  formData.append("description", payload.description ?? "");
  if (payload?.is_deleted === true) {
    formData.append("is_deleted", "true");
  }
  if (contentType === "blog") {
    formData.append("category", payload.category ?? "");
    formData.append("author", payload.author ?? "");
    formData.append("read_time_minutes", String(payload.read_time_minutes ?? 0));
    formData.append("metatitle", payload.metatitle ?? "");
    formData.append("metadescp", payload.metadescp ?? "");
    formData.append("slug", payload.slug ?? "");
  }
  const photos = payload.wedding_photos ?? [];
  photos.forEach((item, i) => {
    if (item.image instanceof File) {
      formData.append(`wedding_photos[${i}][image]`, item.image);
    } else {
      formData.append(`wedding_photos[${i}][image]`, item.image ?? "");
    }
    formData.append(`wedding_photos[${i}][type]`, item.type ?? "gallery");
    if (item.type === "gallery" && item.display_order != null) {
      formData.append(`wedding_photos[${i}][display_order]`, String(item.display_order));
    }
  });
  return formData;
}

function* getRealWeddingsSaga({ payload }) {

  try {
    const response = yield call(callGetRealWeddingsApi, payload ?? {});
    const code = response.httpStatusCode ?? response.status ?? response.statusCode;
    if (code === 200 || code === "200" || code === 201 || code === "201") {
      const data = response.data?.real_weddings ?? response.data ?? response.real_weddings ?? response;
      const rawList = Array.isArray(data) ? data : (data?.data ?? data?.list ?? []);
      const list = Array.isArray(rawList) ? rawList : [];
      yield put(
        onGetRealWeddingsSuccess({
          data: list,
          message: response.message || "Real weddings retrieved",
          status_code: code,
        })
      );
    } else {
      yield put(
        onGetRealWeddingsError({
          data: null,
          message: response.message || "Failed to fetch real weddings",
          status_code: code ?? 400,
        })
      );
    }
  } catch (error) {
    const message =
      error?.response?.data?.message || error?.message || "Something went wrong";
    yield put(
      onGetRealWeddingsError({ data: null, message, status_code: 400 })
    );
  }
}

function* getRealWeddingSaga({ payload }) {
  const lookupKey = typeof payload === "object" ? payload?.id : payload;
  if (!lookupKey) {
    yield put(
      onGetRealWeddingError({
        data: null,
        message: "Missing wedding id",
        status_code: 400,
      })
    );
    return;
  }
  try {
    let resolvedId = lookupKey;
    const maybeNumericId = String(lookupKey).trim();
    const isNumericId = /^\d+$/.test(maybeNumericId);
    if (!isNumericId) {
      const idFromSlug = yield* resolveRealWeddingIdFromSlug(lookupKey);
      if (idFromSlug == null) {
        yield put(
          onGetRealWeddingError({
            data: null,
            message: "Blog not found",
            status_code: 404,
          })
        );
        return;
      }
      resolvedId = idFromSlug;
    }
    const response = yield call(callGetRealWeddingApi, resolvedId);
    const code = response.httpStatusCode ?? response.status ?? response.statusCode;
    if (isSuccessCode(code)) {
      const raw = response.data ?? response;
      const wedding =
        raw?.real_wedding ??
        (Array.isArray(raw) ? raw[0] : raw);
      yield put(
        onGetRealWeddingSuccess({
          data: wedding,
          message: response.message || "Real wedding retrieved",
          status_code: code,
        })
      );
    } else {
      yield put(
        onGetRealWeddingError({
          data: null,
          message: response.message || "Failed to fetch real wedding",
          status_code: code ?? 400,
        })
      );
    }
  } catch (error) {
    const message =
      error?.response?.data?.message || error?.message || "Something went wrong";
    yield put(
      onGetRealWeddingError({ data: null, message, status_code: 400 })
    );
  }
}

function* realWeddingPostSaga({ payload }) {
  try {
    const formData = buildRealWeddingFormData(payload);
    if (payload?.id != null) {
      formData.append("id", String(payload.id));
    }
    const response =
      payload?.id != null
        ? yield call(callRealWeddingUpdateFormDataApi, payload.id, formData)
        : yield call(callRealWeddingPostFormDataApi, formData);
    const code = response.httpStatusCode ?? response.status ?? response.statusCode;
    if (code === 200 || code === "200" || code === 201 || code === "201") {
      const data = response.data ?? response;
      yield put(
        onRealWeddingPostSuccess({
          data,
          message: response.message || "Real wedding added",
          status_code: code,
        })
      );
    } else {
      yield put(
        onRealWeddingPostError({
          data: null,
          message: response.message || "Failed to add real wedding",
          status_code: code ?? 400,
        })
      );
    }
  } catch (error) {
    const message =
      error?.response?.data?.message || error?.message || "Something went wrong";
    yield put(
      onRealWeddingPostError({ data: null, message, status_code: 400 })
    );
  }
}

export default function* realWeddingSaga() {
  yield takeLatest(onGetRealWeddings.type, getRealWeddingsSaga);
  yield takeLatest(onGetRealWedding.type, getRealWeddingSaga);
  yield takeLatest(onRealWeddingPost.type, realWeddingPostSaga);
}
